import type { AppState } from '../../state/appState.js';
import type { DeleteSceneNodeCmd } from '../commandBuilders.js';
import type { SceneNodeId } from '../../scene/sceneState.js';
import type { ActionBase, ActionExecution, EditorAction } from '../commandExecutor.js';
import type { EditorSessionState } from './session.js';
import {
  cloneSceneNodeMap,
  collectRemovedForSubtreeFor,
  collectSceneSubtree,
  deleteSceneSubtreeFor,
  detachSceneNodeFor,
  findSceneParentId,
  findSceneTargetByNodeIdFor,
} from './shared.js';
import {
  captureEditorSessionFor,
  recoverEditorTargetFor,
  resetEditorAfterComponentRemovalFor,
} from './session.js';
import { deleteComponentIfUnreferencedFor } from './sceneOwnership.js';

export interface RemoveFrameAction extends ActionBase<'scene:removeFrame'> {
  nodeId: SceneNodeId;
}

export interface DropOwnedInstanceAction extends ActionBase<'scene:dropOwnedInstance'> {
  nodeId: SceneNodeId;
  componentId: string;
}

export interface DropLayerAction extends ActionBase<'scene:dropLayer'> {
  nodeId: SceneNodeId;
  layerId: string;
}

export interface DropInstanceAction extends ActionBase<'scene:dropInstance'> {
  nodeId: SceneNodeId;
}

export interface SceneRemovalHooks {
  captureEditorSession?: () => EditorSessionState;
  recoverEditorTarget?: () => void;
  resetEditorAfterComponentRemoval?: () => void;
}

function recoverAfterSceneRemoval(appState: AppState, componentId?: string, hooks: SceneRemovalHooks = {}): void {
  if (componentId && appState.activeComponentId === componentId && !appState.componentLibrary[componentId]) {
    if (hooks.resetEditorAfterComponentRemoval) hooks.resetEditorAfterComponentRemoval();
    else resetEditorAfterComponentRemovalFor(appState);
  } else if (appState.activeComponentId && !appState.componentLibrary[appState.activeComponentId]) {
    if (hooks.resetEditorAfterComponentRemoval) hooks.resetEditorAfterComponentRemoval();
    else resetEditorAfterComponentRemovalFor(appState);
  } else {
    if (hooks.recoverEditorTarget) hooks.recoverEditorTarget();
    else recoverEditorTargetFor(appState);
  }
}

export function execRemoveFrameFor(appState: AppState, cmd: RemoveFrameAction): ActionExecution<void> {
  return detachSceneNodeFor(appState, cmd.nodeId)
    ? { result: undefined, undoActions: [], changed: true }
    : { result: undefined, undoActions: [], changed: false };
}

export function execDropLayerFor(
  appState: AppState,
  cmd: DropLayerAction,
  hooks: SceneRemovalHooks = {},
): ActionExecution<void> {
  if (!detachSceneNodeFor(appState, cmd.nodeId)) return { result: undefined, undoActions: [], changed: false };
  delete appState.layerLibrary[cmd.layerId];
  recoverAfterSceneRemoval(appState, undefined, hooks);
  return { result: undefined, undoActions: [], changed: true };
}

export function execDropInstanceFor(appState: AppState, cmd: DropInstanceAction): ActionExecution<void> {
  return detachSceneNodeFor(appState, cmd.nodeId)
    ? { result: undefined, undoActions: [], changed: true }
    : { result: undefined, undoActions: [], changed: false };
}

export function execDropOwnedInstanceFor(
  appState: AppState,
  cmd: DropOwnedInstanceAction,
  hooks: SceneRemovalHooks = {},
): ActionExecution<void> {
  if (!detachSceneNodeFor(appState, cmd.nodeId)) return { result: undefined, undoActions: [], changed: false };
  const hadComponent = Boolean(appState.componentLibrary[cmd.componentId]);
  const removedComponent = deleteComponentIfUnreferencedFor(appState, cmd.componentId, new Set([cmd.nodeId]));
  if (hadComponent && !removedComponent && appState.componentLibrary[cmd.componentId]) {
    return { result: undefined, undoActions: [], changed: true };
  }

  recoverAfterSceneRemoval(appState, cmd.componentId, hooks);

  return { result: undefined, undoActions: [], changed: true };
}

export function execDeleteSceneNodeFor(
  appState: AppState,
  cmd: DeleteSceneNodeCmd,
  hooks: SceneRemovalHooks = {},
): ActionExecution<void> {
  const target = findSceneTargetByNodeIdFor(appState, cmd.nodeId);
  if (!target?.scene.nodes[cmd.nodeId]) return { result: undefined, undoActions: [], changed: false };
  const session = hooks.captureEditorSession?.() ?? captureEditorSessionFor(appState);
  const parentId = findSceneParentId(target.scene, cmd.nodeId);
  const index = parentId
    ? (target.scene.nodes[parentId]?.kind === 'frame' ? target.scene.nodes[parentId].childIds.indexOf(cmd.nodeId) : -1)
    : target.scene.rootIds.indexOf(cmd.nodeId);
  const subtreeNodes = collectSceneSubtree(target.scene, cmd.nodeId);
  const { components: removedComponents, layerDefs: removedLayerDefs } = collectRemovedForSubtreeFor(appState, subtreeNodes);
  const scene = target.scene;
  deleteSceneSubtreeFor(appState, scene, cmd.nodeId, subtreeNodes);

  recoverAfterSceneRemoval(appState, undefined, hooks);

  return {
    result: undefined,
    undoActions: [
      ...Object.values(removedLayerDefs).map(layerDef => ({ type: 'layer:restore', layerDef }) as EditorAction),
      ...Object.values(removedComponents).map(component => ({ type: 'component:restore', component }) as EditorAction),
      { type: 'scene:restoreSubtree', targetComponentId: target.componentId, rootId: cmd.nodeId, parentId, index, nodes: cloneSceneNodeMap(subtreeNodes) },
      { type: 'editor:restoreSession', session },
    ],
    changed: true,
  };
}
